import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import styled from 'styled-components'

import LiveSelect from 'live/components/forms/ui/select'
import { jsonResponseExtractor } from 'live/lib/utils'
import i18n from 'live/services/i18n'

type ImageTypeSelectorProps = {
    selectedWell: string
    imageType: string
    changeConfig: (field: string, value: string) => void
}

const getMappings = _.memoize((wellName: string) =>
    fetch(`/services/plugin-wells-vis/settings/image-log-mappings/well?wellName=${wellName}`).then(
        jsonResponseExtractor
    )
)

const ImageTypeSelector = ({ selectedWell, imageType, changeConfig }: ImageTypeSelectorProps): React.ReactElement => {
    const [mappings, setMappings] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        setLoading(true)
        getMappings(selectedWell)
            .then(setMappings)
            .finally(() => setLoading(false))
    }, [selectedWell])

    const validImageType = mappings.some(mapping => mapping.imageType === imageType)
    const noMappingsFound = !loading && mappings.length === 0

    return (
        <Container>
            <LiveSelect
                className="image-log-image-type-selector"
                label={i18n("Image Type")}
                options={mappings.map(mapping => ({ label: mapping.imageType, value: mapping.imageType }))}
                placeholder={i18n(loading ? 'Loading...' : noMappingsFound ? 'No options found' : 'Select image type')}
                clearable={false}
                value={validImageType ? imageType : null}
                onChange={value => {
                    changeConfig('imageType', value, { updateQuery: true })
                    // TODO: verify if there's a need to consider the (potential) value set by the user for the color scale
                    changeConfig('colorScale', mappings.find(mapping => mapping.imageType === value)?.colorPattern)
                }}
                disabled={loading || noMappingsFound}
            />
            {noMappingsFound && (
                <Exclamation className="text-warning dropdown-toggler--cancel-drag">
                    <i className="far fa-exclamation-circle" />
                    <span>
                        {i18n(
                            'Check if the active run has a linked service provider and if the mappings are registered'
                        )}
                    </span>
                </Exclamation>
            )}
        </Container>
    )
}

export default ImageTypeSelector

const Container = styled.div`
    display: flex;
    flex-direction: column;

    > .live-select {
        margin-bottom: 4px;
    }
`

const Exclamation = styled.div`
    font-size: 12px;
    cursor: default;
    margin-bottom: 10px; 
    span {
        padding-left: 4px;
    }
`
